import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Button, Tag } from "antd";
import { useGetOutgoingPostbackLogQuery } from "api/TenantSlug/OutgoingPostbackLog/hooks";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList";
import { QueryState } from "components/UI/QueryState/QueryState";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { StepLogMessageCell } from "components/UI/Table/UI/StepLogMessageCell/StepLogMessageCell";
import { TenantLink } from "components/UI/TenantLink/TenantLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { FC } from "react";
import { PermissionEnum, SearchFilterEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import OutgoingPostbackLogData = App.Data.OutgoingPostbackLogData;

interface OutgoingPostbackLogDetailDrawerProps {
    logId: string;
}

export const OutgoingPostbackLogDetailDrawer: FC<OutgoingPostbackLogDetailDrawerProps> = ({ logId }) => {
    const { t } = usePaldockTranslation("drawers.outgoing-postback-log-detail");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGA } = usePaldockTranslation("drawers.general-actions");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const { t: tEnum } = usePaldockTranslation("general");
    const drawerId = DrawerId.OutgoingPostbackLogDetail;
    const permissionsUtils = usePermission();
    const canViewAdvertiser = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackLogViewAdvertiser);
    const canViewOutput = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackLogViewOutput);
    const OUTGOING_EID_FILTER = `${TableId.OutgoingPostbackStepLogTable}.eid.${SearchFilterEnum.Equal}`;
    const logDetailIncludes = [
        "outgoingPostback",
        "outgoingPostback.owner",
        "conversionState",
        "conversionState.owner",
        canViewAdvertiser ? "conversionState.advertiser" : null,
        "conversionState.offer",
    ]
        .filter(Boolean)
        .join(",");

    const getOutgoingPostbackLogQuery = useGetOutgoingPostbackLogQuery(logId, {
        filter: {
            include: logDetailIncludes,
        },
    });
    const log = getOutgoingPostbackLogQuery.data?.outgoingPostbackLog;

    const logList = log
        ? PropertiesList.create<OutgoingPostbackLogData>(log, [
              {
                  term: tG("date"),
                  description: ({ created_at }) => (created_at ? <DateTimeCell value={created_at} /> : <EmptyCell />),
              },
              {
                  term: tG("origin_id"),
                  description: ({ conversionState }) =>
                      conversionState?.origin_eid ? (
                          <ShortenedIdWithCopy charactersBetweenDots={35} id={conversionState.origin_eid} />
                      ) : (
                          <EmptyCell />
                      ),
              },
              {
                  term: tG("event_id"),
                  description: ({ conversionState }) =>
                      conversionState?.eid ? (
                          <ShortenedIdWithCopy charactersBetweenDots={35} id={conversionState.eid} />
                      ) : (
                          <EmptyCell />
                      ),
              },
              {
                  term: tG("external_id"),
                  description: ({ advertiser_custom_id }) =>
                      advertiser_custom_id ? (
                          <ShortenedIdWithCopy charactersBetweenDots={35} id={advertiser_custom_id} />
                      ) : (
                          <EmptyCell />
                      ),
              },
              {
                  term: t("fields.transaction"),
                  description: ({ conversionState }) => conversionState?.type ?? <EmptyCell />,
              },
              {
                  term: tG("affiliate"),
                  description: ({ conversionState }) => <EntityLinkCell.Owner entity={conversionState?.owner} />,
              },
              ...(canViewAdvertiser
                  ? [
                        {
                            term: tG("advertiser"),
                            description: ({ conversionState }: OutgoingPostbackLogData) => (
                                <EntityLinkCell.Advertiser entity={conversionState?.advertiser} />
                            ),
                        },
                    ]
                  : []),
              {
                  term: tG("offer"),
                  description: ({ conversionState }) => <EntityLinkCell.Offer entity={conversionState?.offer} />,
              },
              {
                  term: t("fields.name"),
                  description: ({ outgoingPostback }) => outgoingPostback?.name ?? <EmptyCell />,
              },
              {
                  term: t("fields.owner"),
                  description: ({ outgoingPostback }) => outgoingPostback?.owner?.nickname ?? <EmptyCell />,
              },
              {
                  term: t("fields.state"),
                  description: ({ state }) => (
                      <Tag
                          icon={state ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                          color={state ? "green" : "red"}
                      >
                          {state ? tGL("ok") : tGL("fail")}
                      </Tag>
                  ),
              },
              ...(canViewOutput
                  ? [
                        {
                            term: t("fields.output"),
                            description: ({ output }: OutgoingPostbackLogData) =>
                                output ? tEnum(`postback_log_output.${output}`) : <EmptyCell />,
                        },
                        {
                            term: t("fields.message"),
                            description: ({ message, id }: OutgoingPostbackLogData) => (
                                <StepLogMessageCell message={message} id={`message-${id}`} lines={30} />
                            ),
                        },
                    ]
                  : []),
          ])
        : null;

    return (
        <Drawer
            drawerId={drawerId}
            title={<DrawerTitle mainTitle={t("title")} />}
            searchParamsToClean={[`${drawerId}-id`]}
            extraButtons={
                <>
                    <Button size="large" disabled>
                        {tGA("view_scenario_run")}
                    </Button>
                    <TenantLink
                        to="/log/tracking/tracking-api-steps"
                        search={{ [OUTGOING_EID_FILTER]: log?.conversionState?.eid ?? "" }}
                    >
                        <Button size="large">{tGA("view_step_logs")}</Button>
                    </TenantLink>
                </>
            }
        >
            {!log ? (
                <QueryState
                    isLoading={getOutgoingPostbackLogQuery.isLoading}
                    isError={getOutgoingPostbackLogQuery.isError || (!getOutgoingPostbackLogQuery.isLoading && !log)}
                    errorKey="errors.something_went_wrong"
                />
            ) : (
                <DrawerContent className="flex flex-col gap-4 p-4">
                    <DrawerCollapsible title={t("sections.log")}>
                        <PropertiesList list={logList ?? []} />
                    </DrawerCollapsible>
                </DrawerContent>
            )}
        </Drawer>
    );
};
